import type { PnlDataPoint } from '@steward/shared';

export interface PeriodPnlPoint extends PnlDataPoint {
  /** Realized P&L since the period began: 0 before the first day's trades, `total` at the end. */
  periodPnl: number;
}

export interface PeriodPnl {
  points: PeriodPnlPoint[];
  /** What the selected period made or lost. Null when there is nothing to sum. */
  total: number | null;
}

const round2 = (value: number) => Math.round(value * 100) / 100;

/**
 * Rebase the API's all-time cumulative series onto the selected period.
 *
 * `cumulativePnl` starts from everything realized BEFORE the period, so a 30-day chart of an
 * account that is down overall draws a line that ends at the all-time figure and never says what
 * the 30 days themselves made. Summing the daily figures gives the period's own number, and the
 * line drawn from that sum ends on it — the header and the chart cannot disagree.
 */
export function periodPnl(dataPoints: readonly PnlDataPoint[]): PeriodPnl {
  if (dataPoints.length === 0) return { points: [], total: null };

  let running = 0;
  const points = dataPoints.map((point) => {
    running = round2(running + (Number.isFinite(point.realizedPnl) ? point.realizedPnl : 0));
    return { ...point, periodPnl: running };
  });

  return { points, total: running };
}
